import type { PriceReportDefinition } from 'types/price-reports';

// The Price Reports menu (was the legacy OFC page Price Report Center spec A3): a page per report,
// at /pricereports/<slug>, in the menu's order. The reports are Excel workbooks built on the server.
export const PRICE_REPORTS: PriceReportDefinition[] = [
  {
    slug: 'rn-changes',
    title: 'RN-Changes',
    type: 'RnChanges',
    summary:
      'RapNet price changes in the px work format: the current RN prices and their $ and % change by color and clarity, per size.',
    fields: ['date'],
    choice: {
      label: 'Compare with',
      options: [
        { value: 'weeks', label: 'Weeks and months before', request: {} },
        {
          value: 'pricesheet',
          label: 'Rapaport price sheet dates',
          request: { byPriceSheet: true }
        }
      ]
    }
  },
  {
    slug: 'rn-pxrap-changes',
    title: 'RN-PxRap-Changes',
    type: 'RnPxRapChanges',
    summary:
      'How RapNet prices moved after each Rapaport price list change, per shape, with the week-on-week change by color and clarity.',
    fields: ['date']
  },
  {
    slug: 'rapnet-linear',
    title: 'RapNet Linear',
    type: 'RapNetLinear',
    summary:
      'The RapNet price log of a day in a linear format: a row per Rapcode, a sheet per shape.',
    fields: ['date'],
    choice: {
      label: 'Version',
      options: [
        { value: 'standard', label: 'Standard', request: {} },
        { value: 'full', label: 'Full (15 weeks)', request: { type: 'RapNetLinearFull' } }
      ]
    }
  },
  {
    slug: 'rapnet-grid',
    title: 'RapNet Grid',
    type: 'RapNetGrid',
    summary:
      'Discount grids by color and clarity for every size: price list, diamond count, best, 3rd best, average and average 10%.',
    points: [
      'With the snapshot comparison: the average $ and % change and the counts of changed, increased and decreased diamonds against an earlier diamond snapshot.'
    ],
    duration: 'A few minutes',
    fields: ['date', 'compareSnapshot'],
    defaults: { includeDiamondSnapshot: true, compareDaysBack: '7' }
  },
  {
    slug: 'rapnet-prices',
    title: 'RapNet Prices',
    type: 'RapNetPrices',
    summary: 'The RapNet price sheet of a shape on a price log date, in the px work format.',
    fields: ['shapeAndPriceLogDate'],
    choice: {
      label: 'Price',
      options: [
        { value: 'BEST', label: 'Best', request: { category: 'BEST' } },
        { value: 'AVG', label: 'Average', request: { category: 'AVG' } },
        { value: 'BEST10', label: 'Best 10', request: { category: 'BEST10' } },
        { value: 'AVG10PERCENT', label: 'Average 10%', request: { category: 'AVG10PERCENT' } }
      ]
    }
  },
  {
    slug: 'rapnet-summary',
    title: 'RapNet Summary',
    type: 'RapNetSummary',
    summary:
      'The week-on-week $ and % change of the average and average 10% prices per shape, with the 5 and 10 carat price lists.',
    fields: ['date']
  },
  {
    slug: 'rapnet-best-price-changes',
    title: 'RapNet Best Price Changes',
    type: 'RapNetBestPriceChanges',
    summary:
      'The best and 3rd best RapNet prices per shape: their change in a week and the discounts of the last six weeks.',
    fields: ['date']
  },
  {
    slug: 'rapnet-summary-between-dates',
    title: 'RapNet Summary Between Dates',
    type: 'RapNetSummary',
    summary: 'The RapNet Summary of a date compared with an earlier date of your choice.',
    fields: ['dateRange']
  },
  {
    slug: 'rapnet-analysis',
    title: 'RapNet Analysis',
    type: 'RapNetAnalysis',
    summary:
      'A sheet per size comparing two dates: average 10% prices and discounts, stone counts, the price list and the months since its last change.',
    fields: ['dateRange'],
    choice: {
      label: 'Shape',
      options: [
        { value: '1', label: 'Round', request: { shapeId: 1 } },
        { value: '2', label: 'Pear', request: { shapeId: 2 } },
        { value: '3', label: 'Princess', request: { shapeId: 3 } }
      ]
    }
  },
  {
    slug: 'ezi-price-report-1',
    title: 'Ezi Price Report 1',
    type: 'EziPriceReport1',
    summary:
      'One size per sheet: the average price of the cheapest 10% of A3+ diamonds on RapNet, over ten periods from a week to three years.',
    points: [
      'Rap discount and price trends',
      'RapNet price movements against the Rapaport price list',
      'Quantity and total value on RapNet'
    ],
    duration: 'A few minutes',
    fields: ['date', 'onlyFluorNone', 'refresh'],
    choice: {
      label: 'Shape',
      options: [
        { value: '1', label: 'Round', request: { shapeId: 1 } },
        { value: '2', label: 'Pear', request: { shapeId: 2 } }
      ]
    }
  },
  {
    slug: 'ezi-price-report-2',
    title: 'Ezi Price Report 2',
    type: 'EziPriceReport2',
    summary:
      'Round, all sizes on each sheet: the average price of the cheapest 10% of A3+ diamonds on RapNet, a sheet per measure.',
    points: [
      'Rap discount and price trends',
      'RapNet price movements against the Rapaport price list',
      'Quantity and total value on RapNet'
    ],
    duration: 'A few minutes',
    fields: ['date', 'onlyFluorNone', 'refresh'],
    defaults: { onlyFluorNone: true }
  },
  {
    slug: 'ezi-price-report-3',
    title: 'Ezi Price Report 3 – Linear',
    type: 'EziPriceReport3',
    summary:
      'Price trends in a linear format: the average price of the cheapest 10% of A3+ diamonds on RapNet and its change over time.',
    fields: ['date', 'onlyFluorNone'],
    defaults: { onlyFluorNone: true },
    choice: {
      label: 'Shape',
      options: [
        { value: '1', label: 'Round', request: { shapeId: 1 } },
        { value: '2', label: 'Pear', request: { shapeId: 2 } },
        { value: '3', label: 'Princess', request: { shapeId: 3 } }
      ]
    }
  },
  {
    slug: 'snapshot-quantities',
    title: 'Snapshot Quantities by RapSpec',
    type: 'SnapshotQuantities',
    summary:
      'The total value, carats and number of round GIA diamonds of a day’s diamond snapshot, by RapSpec.',
    fields: ['snapshotDate'],
    choice: {
      label: 'Group by',
      options: [
        { value: 'rapcode', label: 'Rapcode', request: {} },
        { value: 'groupindex', label: 'Group index', request: { byGroupIndex: true } }
      ]
    }
  }
];

export const PRICE_REPORTS_ROUTE = '/pricereports';

export const priceReportRoute = (slug: string) => `${PRICE_REPORTS_ROUTE}/${slug}`;

// A report is followed every 3 seconds for up to 30 minutes, as long as the server lets it run.
export const PRICE_REPORT_STATUS_POLLING = {
  intervalMs: 3000,
  maxFailures: 10,
  maxDurationMs: 30 * 60 * 1000
};
